/**
 * Global Search Dialog
 *
 * A command palette-style search dialog for quick navigation.
 * Opens with Cmd/Ctrl + K.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Dialog,
  DialogContent,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Typography,
  Box,
  Chip,
  alpha,
  useTheme,
} from "@mui/material";
import {
  Search as SearchIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  FamilyRestroom as FamilyIcon,
  Class as ClassIcon,
  Receipt as InvoiceIcon,
  Payment as PaymentIcon,
  Dashboard as DashboardIcon,
  Assessment as ReportIcon,
  EventNote as EnrollmentIcon,
  Settings as SettingsIcon,
  WarningAmberRounded as WarningIcon,
  NotificationsOutlined as NotificationsIcon,
} from "@mui/icons-material";
import { NOTIFICATION_SETTINGS_ROLES } from "@/pages/settings/notificationSettingsRows";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { useDebounce } from "@/hooks/useDebounce";
import {
  useRecordSearch,
  MIN_QUERY_LENGTH,
  describeSearchScope,
  searchableKinds,
  type RecordHit,
} from "@/hooks/useRecordSearch";
import type { UserRole } from "@/types/auth";

interface SearchItem {
  id: string;
  label: string;
  description?: string;
  path: string;
  icon: React.ReactNode;
  category: string;
  keywords: string[];
  permission?: string;
  /**
   * Who may see this result. REQUIRED: an entry with no audience used to
   * default to "everyone", so parents were offered Students, Families and
   * Record Payment and were bounced to Access Denied on arrival (audit Q03).
   * Adding a destination now forces you to name its audience.
   */
  roles: UserRole[];
}

/** Everyone who works in the school office. */
const STAFF: UserRole[] = ["super_user", "admin", "principal", "staff"];

export const searchItems: SearchItem[] = [
  // --- Office navigation
  // The school dashboard is built on the admin metrics API (admin/principal);
  // staff's home is today's attendance (utils/homePath.ts).
  { id: "dashboard", label: "Dashboard", path: "/dashboard", icon: <DashboardIcon />, category: "Navigation", keywords: ["home", "main"], permission: "view:school-metrics", roles: STAFF },
  { id: "staff-home", label: "Dashboard", description: "Today's attendance", path: "/attendance", icon: <DashboardIcon />, category: "Navigation", keywords: ["home", "main", "today", "attendance"], roles: ["staff"] },
  { id: "students", label: "Students", description: "Manage student records", path: "/students", icon: <SchoolIcon />, category: "Navigation", keywords: ["pupil", "learner"], roles: STAFF },
  { id: "families", label: "Families", description: "Manage family accounts", path: "/families", icon: <FamilyIcon />, category: "Navigation", keywords: ["parent", "guardian", "household"], roles: STAFF },
  { id: "classes", label: "Classes", description: "Manage class schedules", path: "/classes", icon: <ClassIcon />, category: "Navigation", keywords: ["course", "program"], roles: STAFF },
  { id: "enrollments", label: "Enrollments", description: "Student enrollments", path: "/enrollments", icon: <EnrollmentIcon />, category: "Navigation", keywords: ["registration"], roles: STAFF },
  { id: "invoices", label: "Invoices", description: "Billing invoices", path: "/invoices", icon: <InvoiceIcon />, category: "Navigation", keywords: ["bill", "charge"], roles: STAFF },
  { id: "payments", label: "Payments", description: "Payment records", path: "/payments", icon: <PaymentIcon />, category: "Navigation", keywords: ["transaction", "receipt"], roles: STAFF },
  { id: "reports", label: "Reports", description: "View reports", path: "/reports", icon: <ReportIcon />, category: "Navigation", keywords: ["analytics", "statistics"], roles: ["super_user", "admin", "principal"] },
  { id: "users", label: "Users", description: "Manage system users", path: "/users", icon: <PeopleIcon />, category: "Navigation", keywords: ["staff", "admin"], permission: "manage:users", roles: STAFF },
  { id: "settings", label: "School Settings", description: "School-wide configuration", path: "/admin/tenant-settings", icon: <SettingsIcon />, category: "Navigation", keywords: ["config", "preferences"], roles: ["super_user", "admin"] },

  // --- Office quick actions
  { id: "new-student", label: "Add New Student", path: "/students/new", icon: <SchoolIcon />, category: "Quick Actions", keywords: ["create", "register"], roles: STAFF },
  { id: "new-family", label: "Add New Family", path: "/families/new", icon: <FamilyIcon />, category: "Quick Actions", keywords: ["create"], roles: STAFF },
  { id: "new-enrollment", label: "Create Enrollment", path: "/enrollments/new", icon: <EnrollmentIcon />, category: "Quick Actions", keywords: ["enroll", "register"], roles: STAFF },
  { id: "record-payment", label: "Record Payment", path: "/payments/record", icon: <PaymentIcon />, category: "Quick Actions", keywords: ["collect", "receive"], roles: STAFF },

  // --- Teacher destinations
  { id: "teacher-dashboard", label: "Dashboard", description: "Your teaching day", path: "/teacher/dashboard", icon: <DashboardIcon />, category: "Navigation", keywords: ["home", "main", "today"], roles: ["teacher"] },
  { id: "teacher-classes", label: "My Classes", description: "Classes you teach", path: "/teacher/classes", icon: <ClassIcon />, category: "Navigation", keywords: ["course", "roster"], roles: ["teacher"] },
  { id: "teacher-students", label: "My Students", description: "Students in your classes", path: "/teacher/students", icon: <SchoolIcon />, category: "Navigation", keywords: ["pupil", "learner"], roles: ["teacher"] },
  { id: "teacher-attendance", label: "Attendance", description: "Take and review attendance", path: "/attendance", icon: <EnrollmentIcon />, category: "Quick Actions", keywords: ["present", "absent", "mark"], roles: ["teacher"] },

  // --- Parent destinations
  { id: "parent-dashboard", label: "Dashboard", description: "Your family overview", path: "/parent/dashboard", icon: <DashboardIcon />, category: "Navigation", keywords: ["home", "main"], roles: ["parent"] },
  { id: "parent-students", label: "My Students", description: "Your children", path: "/parent/students", icon: <SchoolIcon />, category: "Navigation", keywords: ["child", "children", "pupil"], roles: ["parent"] },
  { id: "parent-invoices", label: "Invoices", description: "Your invoices", path: "/parent/invoices", icon: <InvoiceIcon />, category: "Navigation", keywords: ["bill", "charge", "balance"], roles: ["parent"] },
  { id: "parent-payments", label: "Payments", description: "Your payment history", path: "/parent/payments", icon: <PaymentIcon />, category: "Navigation", keywords: ["transaction", "receipt"], roles: ["parent"] },
  { id: "parent-statements", label: "Statements", description: "Your account statements", path: "/parent/statements", icon: <InvoiceIcon />, category: "Navigation", keywords: ["account", "ledger"], roles: ["parent"] },
  { id: "parent-apply", label: "Apply for a Program", description: "Add a program for your child", path: "/parent/applications/new", icon: <EnrollmentIcon />, category: "Quick Actions", keywords: ["enroll", "register", "application", "class"], roles: ["parent"] },
  { id: "parent-pay", label: "Make a Payment", description: "Pay an outstanding balance", path: "/parent/payments/make", icon: <PaymentIcon />, category: "Quick Actions", keywords: ["pay", "balance", "checkout"], roles: ["parent"] },
  { id: "parent-payment-methods", label: "Payment Methods", description: "Manage your saved cards", path: "/parent/payment-methods", icon: <PaymentIcon />, category: "Navigation", keywords: ["card", "autopay", "bank"], roles: ["parent"] },

  // --- Your account (every role the notifications preferences API serves;
  // super_user has no tenant and no settings row)
  { id: "notification-settings", label: "Notification Settings", description: "Choose what IlmIQ emails and alerts you about", path: "/settings/notifications", icon: <NotificationsIcon />, category: "Navigation", keywords: ["notifications", "email", "alerts", "preferences", "unsubscribe"], roles: [...NOTIFICATION_SETTINGS_ROLES] },
];

/**
 * Whether a role may be offered this destination. Pure so the role matrix
 * can be asserted without rendering the dialog.
 */
export const canSeeSearchItem = (
  item: SearchItem,
  role: UserRole | undefined,
  hasPermission: (permission: string) => boolean,
): boolean => {
  if (!role || !item.roles.includes(role)) return false;
  if (item.permission && !hasPermission(item.permission)) return false;
  return true;
};

/** Anything selectable in the dialog, record or destination alike. */
interface ResultRow {
  key: string;
  group: string;
  label: string;
  description?: string;
  icon: React.ReactNode;
  path: string;
}

const RECORD_GROUP: Record<RecordHit["kind"], string> = {
  student: "Students",
  family: "Families",
  invoice: "Invoices",
};

const RECORD_ICON: Record<RecordHit["kind"], React.ReactNode> = {
  student: <SchoolIcon />,
  family: <FamilyIcon />,
  invoice: <InvoiceIcon />,
};

/** Plural, lower case, for use inside a sentence. */
const RECORD_NOUN: Record<RecordHit["kind"], string> = {
  student: "students",
  family: "families",
  invoice: "invoices",
};

const GROUP_KIND: Record<string, RecordHit["kind"]> = {
  Students: "student",
  Families: "family",
  Invoices: "invoice",
};

/** "students", "students or invoices", "students, families or invoices" */
const listNouns = (kinds: RecordHit["kind"][]): string => {
  const nouns = kinds.map((k) => RECORD_NOUN[k]);
  if (nouns.length <= 1) return nouns[0] ?? "records";
  return `${nouns.slice(0, -1).join(", ")} or ${nouns[nouns.length - 1]}`;
};

interface GlobalSearchProps {
  open: boolean;
  onClose: () => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ open, onClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const role = useAuthStore((s) => s.user?.role);
  const [query, setQuery] = useState("");
  /**
   * The selection follows the ROW, not its position.
   *
   * Three record searches resolve at different moments, and each one that
   * lands inserts rows above whatever is already drawn. Holding a plain index
   * across that means the highlight silently slides onto a different record
   * and Enter opens it. Null means "the first row", whatever that turns out
   * to be.
   */
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  // Filter items based on permissions and search query
  const filteredItems = useMemo(() => {
    let items = searchItems.filter((item) =>
      canSeeSearchItem(item, role, hasPermission),
    );

    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      items = items.filter(
        (item) =>
          item.label.toLowerCase().includes(lowerQuery) ||
          item.description?.toLowerCase().includes(lowerQuery) ||
          item.keywords.some((kw) => kw.includes(lowerQuery))
      );
    }

    return items;
  }, [query, hasPermission, role]);

  // Records the person may actually be looking for (audit H11). Debounced:
  // every keystroke is not a question worth asking three services.
  const debouncedQuery = useDebounce(query, 250);
  /**
   * An empty box means no results NOW, not in 250ms.
   *
   * The debounce tail outlives the input: reopening the palette — or
   * selecting all and typing one letter — left the previous person's
   * children listed under a box that no longer said their name, with no
   * next search coming to replace them.
   */
  const searchTerm =
    query.trim().length < MIN_QUERY_LENGTH ? "" : debouncedQuery;
  // `open` gates the searches. This component is mounted for the life of the
  // page, so without it the last term stays enabled and every window focus
  // refetches a search the person closed ten minutes ago.
  const { hits, notShown, failedKinds, isSearching, isError, searchedAndFoundNothing } =
    useRecordSearch(searchTerm, open);
  // Say only what this person can find: a parent searches pages, a teacher
  // students and pages. Cheap, so computed every render rather than memoised
  // on a store function whose identity never changes with the role.
  const scope = describeSearchScope(searchableKinds(hasPermission));

  /**
   * One flat, ordered list of everything selectable.
   *
   * Records come first. Someone who types a name wants the person, not the
   * page listing all of them — and the arrow keys have to walk both kinds in
   * the order they are drawn, which is only true if one array decides it.
   */
  const allRows = useMemo<ResultRow[]>(
    () => [
      ...hits.map((hit) => ({
        key: `${hit.kind}-${hit.id}`,
        group: RECORD_GROUP[hit.kind],
        label: hit.label,
        description: hit.context,
        icon: RECORD_ICON[hit.kind],
        path: hit.path,
      })),
      ...filteredItems.map((item) => ({
        key: item.id,
        group: item.category,
        label: item.label,
        description: item.description,
        icon: item.icon,
        path: item.path,
      })),
    ],
    [hits, filteredItems],
  );

  const groupedRows = useMemo(() => {
    const groups: Record<string, ResultRow[]> = {};
    allRows.forEach((row) => {
      if (!groups[row.group]) groups[row.group] = [];
      groups[row.group].push(row);
    });
    return groups;
  }, [allRows]);

  /**
   * Arrow-key order must be the order rows are DRAWN, and grouping reorders
   * them: a destination declared late can be drawn early because its group
   * already exists. Flattening the groups is the only way the two agree.
   */
  const rows = useMemo(() => Object.values(groupedRows).flat(), [groupedRows]);

  /** -1 only when there is nothing to select at all. */
  const selectedIndex = useMemo(() => {
    if (rows.length === 0) return -1;
    const found = selectedKey
      ? rows.findIndex((row) => row.key === selectedKey)
      : -1;
    return found >= 0 ? found : 0;
  }, [rows, selectedKey]);

  /**
   * The live region must exist before it has text; see the announcer below.
   * One tick after the dialog opens is enough for the region to be there
   * first and the text to arrive as a change.
   */
  const [announcerReady, setAnnouncerReady] = useState(false);
  useEffect(() => {
    if (!open) {
      setAnnouncerReady(false);
      return;
    }
    // 100ms, not 0: some screen reader / virtual buffer combinations do not
    // register a region that appears and fills within one macrotask. Nothing
    // is announced this early anyway — the first search has not returned.
    const id = setTimeout(() => setAnnouncerReady(true), 100);
    return () => clearTimeout(id);
  }, [open]);

  // Keep the highlighted row visible: records push the destination list well
  // past the 400px the results box shows.
  const selectedRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // Optional call: jsdom has no layout, so it does not implement this.
    selectedRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [selectedIndex]);

  // Reset on both edges. Clearing only on open leaves the closed dialog
  // holding the last term, which is what kept the searches alive.
  useEffect(() => {
    setQuery("");
    setSelectedKey(null);
  }, [open]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const next = rows[Math.min(selectedIndex + 1, rows.length - 1)];
        if (next) setSelectedKey(next.key);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        const prev = rows[Math.max(selectedIndex - 1, 0)];
        if (prev) setSelectedKey(prev.key);
      } else if (e.key === "Enter") {
        // Always swallow Enter: the input is a search box, and letting it
        // bubble would submit an enclosing form if one is ever added.
        e.preventDefault();
        const row = rows[selectedIndex];
        if (row) {
          navigate(row.path);
          onClose();
        }
      }
    },
    [rows, selectedIndex, navigate, onClose]
  );

  /**
   * What a screen reader is told, and what the box shows when there is
   * nothing to show. One source so the two can never disagree.
   */
  const failureMessage = isError
    ? `Couldn't search ${listNouns(failedKinds)} just now. Your connection may be down — try again in a moment.`
    : undefined;

  const emptyMessage = isSearching
    ? "Searching…"
    : searchedAndFoundNothing
      ? `No pages or records match "${searchTerm.trim()}"`
      : scope.keepTyping &&
          query.trim().length > 0 &&
          query.trim().length < MIN_QUERY_LENGTH
        ? scope.keepTyping
        : `No results found for "${query}"`;

  /**
   * Announced politely. The region below is rendered unconditionally and only
   * its text changes: a live region that appears together with its content is
   * widely not announced at all.
   */
  const announcement = failureMessage
    ? failureMessage
    : isSearching
      ? "Searching"
      : rows.length === 0
        ? emptyMessage
        : [
            `${rows.length} result${rows.length === 1 ? "" : "s"}`,
            // The visible "N more match" hints are aria-hidden decoration on
            // a listbox; without this a screen-reader user hears "5 results"
            // for a 41-match query and stops looking.
            ...(Object.keys(notShown) as RecordHit["kind"][])
              .filter((kind) => notShown[kind] > 0)
              .map((kind) => `${notShown[kind]} more ${RECORD_NOUN[kind]} match`),
          ].join(", ");

  const handleItemClick = (path: string) => {
    navigate(path);
    onClose();
  };

  // Platform detection for shortcut display
  const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);
  const modKey = isMac ? "⌘" : "Ctrl";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="global-search-title"
      aria-describedby="global-search-description"
      slotProps={{
        backdrop: {
          sx: { bgcolor: alpha(theme.palette.common.black, 0.5) },
        },

        paper: {
          sx: {
            mt: "10vh",
            mx: 2,
            maxHeight: "70vh",
            bgcolor: "background.paper",
          },
          role: "search",
        }
      }}>
      <DialogContent sx={{ p: 0 }}>
        {/* Hidden title for screen readers */}
        <Typography id="global-search-title" sx={{ position: "absolute", left: -9999 }}>
          Global Search
        </Typography>
        <Typography id="global-search-description" sx={{ position: "absolute", left: -9999 }}>
          {scope.description} Use arrow keys to navigate, Enter to select.
        </Typography>

        {/*
          The announcer, mounted before it has anything to say.
          
          A live region created in the same commit as its text is widely not
          announced, and MUI marks everything OUTSIDE an open modal
          aria-hidden — so it cannot live outside the dialog either. It goes
          in empty and fills a tick later. The visible messages below are
          aria-hidden so the announcement happens once.
        */}
        {/* The global .sr-only class (App.tsx): in sx a bare `width: 1` is 100%. */}
        <Box role="status" aria-live="polite" className="sr-only">
          {announcerReady ? announcement : ""}
        </Box>

        {/* Search Input */}
        <TextField
          fullWidth
          autoFocus
          placeholder={scope.placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSelectedKey(null);
          }}
          onKeyDown={handleKeyDown}
          sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}
          slotProps={{
            // These belong on the <input> itself. Passed to TextField they
            // land on the wrapper, where no screen reader looks for them —
            // aria-activedescendant in particular is the only thing that
            // announces the highlight moving.
            htmlInput: {
              role: "combobox",
              "aria-expanded": rows.length > 0,
              "aria-label": scope.ariaLabel,
              "aria-autocomplete": "list",
              "aria-controls": "search-results-list",
              "aria-activedescendant": rows[selectedIndex]
                ? `search-item-${rows[selectedIndex].key}`
                : undefined,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Chip
                    label="esc"
                    size="small"
                    aria-hidden="true"
                    sx={{ fontFamily: "monospace", fontSize: 11 }}
                  />
                </InputAdornment>
              ),
              sx: {
                fontSize: "1.1rem",
                "& fieldset": { border: "none" },
              },
            }
          }}
        />

        {/*
          A failed search must never be reported as an absent student. The
          strip shows whether or not any destinations matched, because a staff
          member who types a name and sees three menu items is entitled to
          know the record search did not run.
        */}
        {failureMessage && (
          <Box
            aria-hidden="true"
            sx={{
              px: 2,
              py: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1,
              bgcolor: alpha(theme.palette.warning.main, 0.12),
            }}
          >
            <WarningIcon fontSize="small" color="warning" />
            <Typography variant="body2" sx={{ color: "text.primary" }}>
              {failureMessage}
            </Typography>
          </Box>
        )}

        {/* Results */}
        {rows.length === 0 && !isError ? (
          <Box sx={{ p: 4, textAlign: "center" }} aria-hidden="true">
            <Typography sx={{ color: "text.secondary" }}>
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <List
            id="search-results-list"
            role="listbox"
            aria-label="Search results"
            sx={{ py: 1, maxHeight: 400, overflow: "auto" }}
          >
            {Object.entries(groupedRows).map(([category, items]) => (
              <React.Fragment key={category}>
                <Typography
                  variant="overline"
                  role="presentation"
                  aria-hidden="true"
                  sx={{
                    color: "text.secondary",
                    px: 2,
                    py: 0.5,
                    display: "block"
                  }}>
                  {category}
                </Typography>
                {items.map((item) => {
                  const globalIndex = rows.indexOf(item);
                  const isSelected = globalIndex === selectedIndex;

                  return (
                    // The option is the BUTTON, not the li: aria-activedescendant
                    // has to name an element that is actually an option, and an
                    // interactive control nested inside one is invalid.
                    <ListItem key={item.key} disablePadding role="none">
                      <ListItemButton
                        role="option"
                        aria-selected={isSelected}
                        // The input keeps focus and aria-activedescendant
                        // moves the highlight, so Tab must not walk all
                        // fourteen rows.
                        tabIndex={-1}
                        ref={isSelected ? selectedRef : undefined}
                        id={`search-item-${item.key}`}
                        selected={isSelected}
                        onClick={() => handleItemClick(item.path)}
                        sx={{
                          mx: 1,
                          borderRadius: 1,
                          "&.Mui-selected": {
                            bgcolor: alpha(theme.palette.primary.main, 0.12),
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.label}
                          secondary={item.description}
                          slotProps={{
                            primary: { sx: { fontWeight: isSelected ? 600 : 400 } }
                          }}
                        />
                        {isSelected && (
                          <Chip
                            label="↵"
                            size="small"
                            sx={{ fontFamily: "monospace" }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {/*
                  The server orders by surname, not relevance, so the sixth
                  Ahmad is simply absent. Saying so is the difference between
                  a short list and a wrong answer.
                */}
                {GROUP_KIND[category] && notShown[GROUP_KIND[category]] > 0 && (
                  <ListItem role="none" sx={{ pl: 7, pt: 0, pb: 1 }}>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {notShown[GROUP_KIND[category]]} more{" "}
                      {RECORD_NOUN[GROUP_KIND[category]]} match — type more of
                      the name to narrow it down
                    </Typography>
                  </ListItem>
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Footer hint */}
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 2,
            justifyContent: "flex-end",
          }}
        >
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            <kbd>↑↓</kbd> Navigate
          </Typography>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            <kbd>↵</kbd> Select
          </Typography>
          <Typography variant="caption" sx={{
            color: "text.secondary"
          }}>
            <kbd>{modKey}+/</kbd> Shortcuts
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalSearch;
